import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Save,
  Search,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import StyledSelect from './StyledSelect';
import {
  getOrgMembers,
  getOrgPermissionHierarchy,
  getOrganizationPermissions,
  getOrganizationRolePermissions,
  getOrganizationUserPermissions,
  updateOrganizationUserPermissions,
  updateOrganizationRolePermissions,
} from '../api/api';
import { useAuth } from '../context/AuthContext';
import RoleResponsibilityCard from './RoleResponsibilityCard';
import { ROLE_LABELS, getAssignableRoles } from '../config/permissionConfig';

const CARD = { borderColor: '#f4d6df', background: '#fff' };

const HIDDEN_ROLE_KEYS = new Set(['organization_head', 'org_head']);

function isHiddenOrgRole(role) {
  return HIDDEN_ROLE_KEYS.has(String(role || '').toLowerCase());
}

function humanizeRole(role) {
  const raw = String(role || '').trim();
  if (!raw) return 'Employee';

  return (
    ROLE_LABELS[raw] ||
    raw
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

export default function PermissionManagerContent({ initialUserId = '' }) {
  const { activeOrg, orgRole, refreshProfile } = useAuth();
  const roleRequestRef = useRef(0);
  const userRequestRef = useRef(0);
  const bootstrapRequestRef = useRef(0);

  const [roles, setRoles] = useState([]);
  const [members, setMembers] = useState([]);

  const [roleName, setRoleName] = useState('employee');
  const [selectedUserId, setSelectedUserId] = useState(
    initialUserId ? String(initialUserId) : ''
  );

  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [permissionMap, setPermissionMap] = useState({});
  const [basePermissionMap, setBasePermissionMap] = useState({});
  const [usingDefaults, setUsingDefaults] = useState(false);

  const [selectedUserRole, setSelectedUserRole] = useState('');
  const [selectedUserResponsibilities, setSelectedUserResponsibilities] = useState([]);
  const [userGroupedPermissions, setUserGroupedPermissions] = useState({});
  const [userPermissionMap, setUserPermissionMap] = useState({});
  const [baseUserPermissionMap, setBaseUserPermissionMap] = useState({});

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [hierarchyData, setHierarchyData] = useState(null);

  const assignableRoleKeys = useMemo(() => {
    const fromApi = hierarchyData?.assignable_roles;
    if (Array.isArray(fromApi) && fromApi.length > 0) {
      return fromApi;
    }
    return getAssignableRoles(orgRole || 'admin');
  }, [hierarchyData, orgRole]);

  const manageableRoles = useMemo(
    () => roles.filter((role) => assignableRoleKeys.includes(role.key)),
    [roles, assignableRoleKeys]
  );

  const availableMembers = useMemo(
    () => members.filter((member) => !isHiddenOrgRole(member.org_role)),
    [members]
  );

  const combinedMemberOptions = useMemo(
    () =>
      availableMembers.map((member) => {
        const displayName = member.name || member.email || `User ${member.id}`;
        const displayRole = humanizeRole(member.org_role || 'employee');

        return {
          value: String(member.id),
          label: `${displayName} · ${displayRole}`,
        };
      }),
    [availableMembers]
  );

  const selectedMember = useMemo(
    () =>
      availableMembers.find((member) => String(member.id) === String(selectedUserId)) ||
      null,
    [availableMembers, selectedUserId]
  );

  const selectedRoleLabel = useMemo(
    () => humanizeRole(roleName || selectedUserRole || 'employee'),
    [roleName, selectedUserRole]
  );

  const selectedCount = useMemo(
    () => Object.values(permissionMap).filter(Boolean).length,
    [permissionMap]
  );
  const totalCount = useMemo(
    () => Object.keys(permissionMap).length,
    [permissionMap]
  );

  const userSelectedCount = useMemo(
    () => Object.values(userPermissionMap).filter(Boolean).length,
    [userPermissionMap]
  );
  const userTotalCount = useMemo(
    () => Object.keys(userPermissionMap).length,
    [userPermissionMap]
  );

  const hasRoleChanges = useMemo(
    () => hasMapChanged(basePermissionMap, permissionMap),
    [basePermissionMap, permissionMap]
  );
  const hasUserChanges = useMemo(
    () => hasMapChanged(baseUserPermissionMap, userPermissionMap),
    [baseUserPermissionMap, userPermissionMap]
  );
  const hasAnyChanges = hasRoleChanges || (selectedUserId && hasUserChanges);

  const filteredRoleGroups = useMemo(
    () => filterGroups(groupedPermissions, query),
    [groupedPermissions, query]
  );
  const filteredUserGroups = useMemo(
    () => filterGroups(userGroupedPermissions, query),
    [userGroupedPermissions, query]
  );

  const loadRolePermissions = async (nextRole) => {
    const requestId = ++roleRequestRef.current;
    const res = await getOrganizationRolePermissions(nextRole);
    if (requestId !== roleRequestRef.current) return;

    const payload = res?.data?.data || {};
    const grouped = payload.grouped_permissions || {};
    const nextMap = flattenGroupedPermissions(grouped);

    setGroupedPermissions(grouped);
    setPermissionMap(nextMap);
    setBasePermissionMap(nextMap);
    setUsingDefaults(payload.using_defaults === true);
  };

  const loadMembers = async (orgId) => {
    const memberRes = await getOrgMembers(orgId, { page: 1, limit: 500 });
    const rows = memberRes?.data?.data?.members || [];
    setMembers(rows);

    if (!selectedUserId) {
      const firstSelectable = rows.find((member) => !isHiddenOrgRole(member.org_role));
      if (firstSelectable?.id) {
        setSelectedUserId(String(firstSelectable.id));
      }
    }

    return rows;
  };

  const loadUserPermissions = async (userId) => {
    const requestId = ++userRequestRef.current;

    if (!userId) {
      if (requestId !== userRequestRef.current) return;
      setUserGroupedPermissions({});
      setUserPermissionMap({});
      setBaseUserPermissionMap({});
      setSelectedUserRole('');
      setSelectedUserResponsibilities([]);
      return;
    }

    const res = await getOrganizationUserPermissions(userId);
    if (requestId !== userRequestRef.current) return;

    const payload = res?.data?.data || {};
    const grouped = payload.grouped_permissions || {};
    const nextMap = flattenGroupedPermissions(grouped);

    setSelectedUserRole(payload.role_name || 'employee');
    setSelectedUserResponsibilities(
      Array.isArray(payload.role_responsibilities)
        ? payload.role_responsibilities
        : []
    );
    setUserGroupedPermissions(grouped);
    setUserPermissionMap(nextMap);
    setBaseUserPermissionMap(nextMap);
  };

  useEffect(() => {
    if (!initialUserId) return;
    setSelectedUserId(String(initialUserId));
  }, [initialUserId]);

  useEffect(() => {
    if (!activeOrg?.id) return;

    const load = async () => {
      const requestId = ++bootstrapRequestRef.current;
      setLoading(true);
      setError('');

      try {
        const [permissionRes, hierarchyRes] = await Promise.all([
          getOrganizationPermissions(),
          getOrgPermissionHierarchy().catch(() => null),
        ]);

        if (requestId !== bootstrapRequestRef.current) return;

        const payload = permissionRes?.data?.data || {};
        const hierarchyPayload = hierarchyRes?.data?.data || null;

        setHierarchyData(hierarchyPayload);

        const nextRoles = Array.isArray(payload.roles) ? payload.roles : [];
        setRoles(nextRoles);

        const apiAssignable = Array.isArray(hierarchyPayload?.assignable_roles)
          ? hierarchyPayload.assignable_roles
          : getAssignableRoles(orgRole || 'admin');

        const allowedRoles = nextRoles.filter((role) =>
          apiAssignable.includes(role.key)
        );
        const defaultRole = allowedRoles[0]?.key || nextRoles[0]?.key || 'employee';
        const effectiveRole = roleName || defaultRole;

        if (!roleName) {
          setRoleName(effectiveRole);
        }

        await Promise.all([loadRolePermissions(effectiveRole), loadMembers(activeOrg.id)]);
      } catch (requestError) {
        if (requestId !== bootstrapRequestRef.current) return;
        setError(
          requestError?.response?.data?.message ||
            'Failed to load organization permissions.'
        );
      } finally {
        if (requestId !== bootstrapRequestRef.current) return;
        setLoading(false);
      }
    };

    load();
  }, [activeOrg?.id]);

  useEffect(() => {
    if (!selectedUserId || loading) return;

    const member = availableMembers.find(
      (item) => String(item.id) === String(selectedUserId)
    );

    const nextRole = member?.org_role || 'employee';

    if (nextRole && nextRole !== roleName) {
      setRoleName(nextRole);
      return;
    }

    if (
      manageableRoles.length > 0 &&
      !manageableRoles.some((role) => role.key === nextRole)
    ) {
      setRoleName(manageableRoles[0].key);
      return;
    }

    loadRolePermissions(nextRole).catch((requestError) => {
      setError(
        requestError?.response?.data?.message ||
          'Failed to load role permissions.'
      );
    });
  }, [selectedUserId, loading, availableMembers, roleName, manageableRoles]);

  useEffect(() => {
    loadUserPermissions(selectedUserId).catch((requestError) => {
      setError(
        requestError?.response?.data?.message ||
          'Failed to load user permissions.'
      );
    });
  }, [selectedUserId]);

  const onToggleRolePermission = (permissionKey) => {
    setPermissionMap((prev) => ({ ...prev, [permissionKey]: !prev[permissionKey] }));
    setMessage('');
  };

  const onToggleUserPermission = (permissionKey) => {
    setUserPermissionMap((prev) => ({ ...prev, [permissionKey]: !prev[permissionKey] }));
    setMessage('');
  };

  const onSaveAll = async () => {
    if (!hasAnyChanges) {
      setMessage('No pending changes to save.');
      setError('');
      return;
    }

    setSaving(true);
    setMessage('');
    setError('');

    try {
      if (hasRoleChanges) {
        await updateOrganizationRolePermissions({
          role_name: roleName,
          permissions: permissionMap,
        });
      }

      if (selectedUserId && hasUserChanges) {
        await updateOrganizationUserPermissions(selectedUserId, {
          permissions: userPermissionMap,
        });
      }

      await refreshProfile();

      if (hasRoleChanges) {
        await loadRolePermissions(roleName);
      }

      if (selectedUserId && hasUserChanges) {
        await loadUserPermissions(selectedUserId);
      }

      setMessage('Permissions saved successfully. Access updates are now active.');
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          'Failed to save permission updates.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      <section className="rounded-3xl border p-5 shadow-sm" style={CARD}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-slate-500">
              <ShieldCheck size={18} style={{ color: '#c44569' }} />
              <p className="text-xs uppercase tracking-[0.2em] font-semibold">
                Permission Management
              </p>
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 mt-2">
              Access Control Matrix
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage role defaults and member overrides in one place for{' '}
              {activeOrg?.name || 'your organization'}.
            </p>
          </div>

          <div
            className="rounded-2xl border px-4 py-3 text-right min-w-[160px]"
            style={{ borderColor: '#f5dde4', background: '#fff8fa' }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">
              Role Allowed
            </p>
            <p className="text-2xl font-bold text-slate-900 mt-1">
              {selectedCount}
              <span className="text-base text-slate-400">/{totalCount}</span>
            </p>
          </div>
        </div>
      </section>

      {message ? <Banner tone="success" text={message} /> : null}
      {error ? <Banner tone="error" text={error} /> : null}

      {!loading && usingDefaults ? (
        <div
          className="rounded-2xl border px-4 py-3 flex items-start gap-3"
          style={{ borderColor: '#fbbf24', background: '#fffbeb' }}
        >
          <AlertTriangle
            size={18}
            className="shrink-0 mt-0.5"
            style={{ color: '#d97706' }}
          />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              Role is using system defaults
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              No organization-specific permissions have been saved for this role
              yet. Toggle the permissions below and click{' '}
              <strong>Save All Permission Changes</strong> to commit explicit
              access rules. Once saved, only the permissions you enable will
              appear in this role&apos;s navigation.
            </p>
          </div>
        </div>
      ) : null}

      {!loading && !usingDefaults ? (
        <div
          className="rounded-2xl border px-4 py-3 flex items-start gap-3"
          style={{ borderColor: '#86efac', background: '#f0fdf4' }}
        >
          <CheckCircle2
            size={18}
            className="shrink-0 mt-0.5"
            style={{ color: '#16a34a' }}
          />
          <div>
            <p className="text-sm font-semibold text-green-800">
              Explicit permissions active
            </p>
            <p className="text-xs text-green-700 mt-0.5">
              This role has organization-specific permissions configured.
              Members with this role only see the screens you have explicitly
              enabled below.
            </p>
          </div>
        </div>
      ) : null}

      <section className="rounded-3xl border p-4 sm:p-5 shadow-sm space-y-4" style={CARD}>
        <div
          className="rounded-2xl border px-4 py-3"
          style={{ borderColor: '#fde68a', background: '#fffbeb' }}
        >
          <p className="text-xs uppercase tracking-[0.16em] text-amber-700 font-semibold">
            Role Hierarchy Guard
          </p>
          <p className="text-sm text-amber-900 mt-1">
            Your role:{' '}
            <strong>{ROLE_LABELS[orgRole] || (orgRole || 'Unknown').replace(/_/g, ' ')}</strong>. You can only configure roles below your authority level.
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Assignable roles:{' '}
            {manageableRoles.length > 0
              ? manageableRoles.map((role) => role.label).join(', ')
              : 'No assignable roles available.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-2">
              Member & Role
            </label>
            <StyledSelect
              value={selectedUserId}
              onChange={setSelectedUserId}
              options={combinedMemberOptions}
              placeholder="Select member with role"
              searchable
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Module, action, or key"
                className="w-full h-11 rounded-xl border pl-9 pr-4 text-sm outline-none focus:ring-4 focus:ring-rose-100 focus:border-rose-300"
                style={{ borderColor: '#f4d6df' }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div
            className="rounded-2xl border px-4 py-4 shadow-sm"
            style={{
              borderColor: '#e7edf8',
              background:
                'linear-gradient(135deg, #f8fbff 0%, #ffffff 55%, #f7fbff 100%)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex h-11 w-11 items-center justify-center rounded-2xl"
                style={{
                  background: 'linear-gradient(135deg, #c44569 0%, #e56d8a 100%)',
                  boxShadow: '0 10px 24px rgba(196, 69, 105, 0.18)',
                }}
              >
                <UserRound size={19} color="#fff" />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                  Selected Member
                </p>
                <p className="mt-1 text-sm font-bold text-slate-900 truncate">
                  {selectedMember?.name || selectedMember?.email || 'No member selected'}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {selectedMember?.email || `User ID: ${selectedUserId || '-'}`}
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  background: '#fff1f5',
                  color: '#9d174d',
                  border: '1px solid #f9a8d4',
                }}
              >
                {selectedMember ? humanizeRole(selectedMember.org_role) : 'Role not selected'}
              </span>

              <span
                className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  background: '#f8fafc',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                }}
              >
                {selectedUserId ? 'Override access enabled' : 'No member selected'}
              </span>
            </div>
          </div>

          <div
            className="rounded-2xl border px-4 py-4 shadow-sm"
            style={{
              borderColor: '#e7edf8',
              background:
                'linear-gradient(135deg, #fff7fa 0%, #ffffff 55%, #fff7fa 100%)',
            }}
          >
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
              Effective Role Template
            </p>
            <p className="mt-1 text-base font-bold text-slate-900">{selectedRoleLabel}</p>
            <p className="mt-1 text-xs text-slate-500">
              This template controls the permission defaults loaded for the selected member.
            </p>

            <div className="mt-3 flex flex-wrap gap-2">
              {(selectedUserResponsibilities || []).slice(0, 6).map((item) => (
                <span
                  key={item}
                  className="rounded-full border px-2.5 py-1 text-xs text-slate-600"
                  style={{ borderColor: '#d8e1f3', background: '#fff' }}
                >
                  {item}
                </span>
              ))}
            </div>

            <p className="mt-3 text-xs text-slate-500">
              Member Allowed: {userSelectedCount}/{userTotalCount}
            </p>
          </div>
        </div>

        <RoleResponsibilityCard
          roleName={roleName}
          roleLabel={selectedRoleLabel}
          isBeingConfigured
        />
      </section>

      {loading ? (
        <section
          className="rounded-3xl border p-8 shadow-sm flex items-center justify-center gap-3"
          style={CARD}
        >
          <Loader2 className="animate-spin" size={20} style={{ color: '#c44569' }} />
          <span className="text-sm text-slate-500">Loading permission tables...</span>
        </section>
      ) : (
        <>
          <PermissionTableSection
            title="Role Permission Defaults"
            subtitle="Applies to all users with the selected role"
            groups={filteredRoleGroups}
            permissionMap={permissionMap}
            onToggle={onToggleRolePermission}
          />

          <PermissionTableSection
            title="User Permission Overrides"
            subtitle="Overrides only the selected member"
            groups={filteredUserGroups}
            permissionMap={userPermissionMap}
            onToggle={onToggleUserPermission}
            emptyText={
              selectedUserId
                ? 'No override permissions matched your search.'
                : 'Select a member to manage user-level overrides.'
            }
          />
        </>
      )}

      <section className="fixed left-0 right-0 bottom-0 z-20 border-t bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-xs sm:text-sm text-slate-500">
            {hasAnyChanges
              ? 'You have unsaved permission changes.'
              : 'All permission changes are saved.'}
          </p>
          <button
            type="button"
            onClick={onSaveAll}
            disabled={saving || loading || !hasAnyChanges}
            className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60 transition"
            style={{ background: '#c44569' }}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving All Changes...' : 'Save All Permission Changes'}
          </button>
        </div>
      </section>
    </div>
  );
}

function PermissionTableSection({
  title,
  subtitle,
  groups,
  permissionMap,
  onToggle,
  emptyText = 'No permissions available for this selection.',
}) {
  if (Object.keys(groups).length === 0) {
    return (
      <section className="rounded-3xl border p-6 shadow-sm text-center" style={CARD}>
        <p className="text-sm text-slate-500">{emptyText}</p>
      </section>
    );
  }

  return (
    <section className="rounded-3xl border p-4 sm:p-5 shadow-sm space-y-4" style={CARD}>
      <div>
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </div>

      <div className="space-y-4">
        {Object.entries(groups).map(([module, items]) => (
          <div
            key={module}
            className="rounded-2xl border overflow-hidden"
            style={{ borderColor: '#f3dee5' }}
          >
            <div
              className="px-4 py-3 border-b flex items-center justify-between"
              style={{ borderColor: '#f3dee5', background: '#fff7fa' }}
            >
              <p className="text-sm font-bold text-slate-900">
                {module.replace(/_/g, ' ')}
              </p>
              <p className="text-xs text-slate-500">
                {items.filter((item) => permissionMap[item.permission_key]).length}/{items.length}{' '}
                allowed
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-400 bg-white">
                    <th className="px-4 py-3">Permission</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Granted By</th>
                    <th className="px-4 py-3 text-right">Allowed</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const allowed = !!permissionMap[item.permission_key];
                    return (
                      <tr
                        key={`${module}-${item.permission_key}`}
                        className="border-t"
                        style={{ borderColor: '#f8ebf0' }}
                      >
                        <td className="px-4 py-3 align-top">
                          <div className="font-semibold text-slate-900">{item.label}</div>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            {item.permission_key}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-500 align-top">
                          {item.description || 'No description provided.'}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <GrantedByBadge
                            role={item.granted_by_role}
                            label={item.granted_by_label}
                          />
                        </td>
                        <td className="px-4 py-3 text-right align-top">
                          <Toggle
                            checked={allowed}
                            onToggle={() => onToggle(item.permission_key)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Toggle({ checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition"
      style={{
        background: checked ? '#dcfce7' : '#f8fafc',
        color: checked ? '#166534' : '#64748b',
        border: `1px solid ${checked ? '#86efac' : '#cbd5e1'}`,
      }}
    >
      <span
        className="relative inline-flex h-5 w-10 items-center rounded-full transition"
        style={{ background: checked ? '#16a34a' : '#cbd5e1' }}
      >
        <span
          className="inline-block h-4 w-4 transform rounded-full bg-white transition"
          style={{ transform: checked ? 'translateX(22px)' : 'translateX(2px)' }}
        />
      </span>
      {checked ? 'Allowed' : 'Blocked'}
    </button>
  );
}

function Banner({ tone, text }) {
  const styles =
    tone === 'success'
      ? {
          borderColor: '#a7f3d0',
          background: '#ecfdf5',
          color: '#047857',
        }
      : {
          borderColor: '#f9b8ca',
          background: '#fce0e8',
          color: '#c44569',
        };

  return (
    <div className="rounded-2xl border px-4 py-3 text-sm" style={styles}>
      {text}
    </div>
  );
}

function GrantedByBadge({ role, label }) {
  if (role === 'organization_head') {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
        style={{
          background: '#fce7f3',
          color: '#9d174d',
          border: '1px solid #f9a8d4',
        }}
      >
        {label || 'Org Head'}
      </span>
    );
  }

  if (role === 'admin') {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
        style={{
          background: '#eff6ff',
          color: '#1e40af',
          border: '1px solid #bfdbfe',
        }}
      >
        {label || 'Admin'}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{
        background: '#f8fafc',
        color: '#64748b',
        border: '1px solid #e2e8f0',
      }}
    >
      System Default
    </span>
  );
}

function flattenGroupedPermissions(grouped = {}) {
  const map = {};
  Object.values(grouped).forEach((items) => {
    (items || []).forEach((item) => {
      map[item.permission_key] = !!item.is_allowed;
    });
  });
  return map;
}

function filterGroups(grouped = {}, query = '') {
  const loweredQuery = query.trim().toLowerCase();
  const result = {};

  Object.entries(grouped).forEach(([module, items]) => {
    const filtered = (items || []).filter((item) => {
      if (!loweredQuery) return true;
      return [item.label, item.permission_key, item.description, module]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(loweredQuery));
    });

    if (filtered.length > 0) {
      result[module] = filtered;
    }
  });

  return result;
}

function hasMapChanged(baseMap = {}, nextMap = {}) {
  const keys = new Set([...Object.keys(baseMap), ...Object.keys(nextMap)]);
  for (const key of keys) {
    if (!!baseMap[key] !== !!nextMap[key]) {
      return true;
    }
  }
  return false;
}
